/**
 * `/oms/customers` — cross-customer OMS list at admin.orboto.io
 * (OCP-133). Operator's entry-point for triaging OMS-side issues.
 *
 * Wire chain:
 *   admin SPA (this page)
 *     → OCP-API `/v1/admin/oms/customers` (operator-JWT gated)
 *     → OcpOmsAdminClient (HMAC-signed, OCP-149)
 *     → OMS `/api/v1/admin/customers` (mail.orboto.io)
 *
 * Three notable states beyond happy-path:
 *   - 503 `oms_not_configured` → renders an explicit "OMS bridge not
 *     wired" empty state pointing the operator at OCP-148 follow-up
 *   - 401 from OMS (HMAC drift) → surfaces the OMS-side detail so the
 *     operator can see "PORTAL_HMAC_SECRET mismatch between stacks"
 *     directly rather than a generic error
 *   - empty result set → distinguishes from "no search match" so first-
 *     run operators don't think the page is broken
 *
 * Search is debounced 300ms. Cursor pagination uses opaque OMS-issued
 * cursor strings (no client-side computation).
 *
 * Row click -> `/oms/customers/:id` (CustomerDetailPage, admin App.tsx).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api, ApiError } from '../../lib/api';
import { fmtDate } from '../../lib/format-time';

interface OmsCustomer {
  id: string;
  email: string;
  portal_customer_id?: string | null;
  plan_tier?: string | null;
  status?: string | null;
  created_at?: string | null;
  monthly_usage_used?: number | null;
  monthly_usage_total?: number | null;
}

interface CustomersResponse {
  customers: OmsCustomer[];
  cursor?: string | null;
}

interface ErrorPayload {
  error: string;
  detail?: string;
}

const SEARCH_DEBOUNCE_MS = 300;

export function OmsCustomersPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [data, setData] = useState<CustomersResponse | null>(null);
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'error' | 'unconfigured'>(
    'idle',
  );
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  // Debounce the search input → debounced value drives the fetch.
  // Reset cursor when search changes (pagination context becomes stale).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(searchInput.trim());
      setCursor(undefined);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (searchDebounced) params.set('search', searchDebounced);
    if (cursor) params.set('cursor', cursor);
    params.set('limit', '50');
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }, [searchDebounced, cursor]);

  const load = useCallback(async () => {
    setLoadState('loading');
    setErrorDetail(null);
    try {
      const r = await api<CustomersResponse>(`/v1/admin/oms/customers${queryString}`);
      setData(r);
      setLoadState('idle');
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setLoadState('unconfigured');
        return;
      }
      if (err instanceof ApiError) {
        const body = err.body as ErrorPayload | null;
        setErrorDetail(
          body?.detail
            ? `${body.error}: ${body.detail.slice(0, 200)}`
            : `${body?.error ?? `HTTP ${err.status}`}`,
        );
      } else {
        setErrorDetail(err instanceof Error ? err.message : 'Unknown error');
      }
      setLoadState('error');
    }
  }, [queryString]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">orboto Mail · Customers</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Cross-customer view of every orboto Mail Service account. Search by email,
          plan tier, or portal customer ID. Click a row to see API keys, sender
          domains, sends, quota, and suppression details.
        </p>
      </header>

      <div className="mb-4 flex items-center gap-3">
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by email or portal customer ID"
          className="w-80 rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          aria-label="Search orboto Mail customers"
        />
        {loadState === 'loading' && (
          <span className="text-xs text-gray-500">Loading…</span>
        )}
      </div>

      {loadState === 'unconfigured' && <UnconfiguredEmptyState />}
      {loadState === 'error' && (
        <ErrorPanel detail={errorDetail} onRetry={() => void load()} />
      )}
      {(loadState === 'idle' || loadState === 'loading') && data && (
        <CustomerTable
          customers={data.customers}
          onRowClick={(c) => navigate(`/oms/customers/${c.id}`)}
        />
      )}
      {(loadState === 'idle' || loadState === 'loading') && data?.cursor && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => setCursor(data.cursor ?? undefined)}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Load more →
          </button>
        </div>
      )}
    </div>
  );
}

function UnconfiguredEmptyState() {
  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-6">
      <h2 className="text-base font-semibold text-amber-900 dark:text-amber-200">orboto Mail bridge not configured</h2>
      <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">
        The orboto Control → orboto Mail env-bridge (<code>OMS_API_BASE</code> +{' '}
        <code>OMS_INTERNAL_HMAC_SECRET</code>) is not set on this stack. Customer
        data will appear here once Coolify is updated and the api container
        redeploys.
      </p>
      <p className="mt-3 text-xs text-amber-700 dark:text-amber-300">
        Operator action: set <code>OMS_API_BASE=https://mail.orboto.io</code> and{' '}
        <code>OMS_INTERNAL_HMAC_SECRET</code> (same value as orboto Mail's{' '}
        <code>PORTAL_HMAC_SECRET</code>) on the orboto Control Coolify project, then trigger
        a redeploy.
      </p>
    </div>
  );
}

function ErrorPanel({
  detail,
  onRetry,
}: {
  detail: string | null;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-red-900 dark:text-red-200">
            Could not load orboto Mail customers
          </h2>
          {detail && (
            <p className="mt-1 break-all text-xs text-red-800 dark:text-red-300">{detail}</p>
          )}
          <p className="mt-2 text-xs text-red-700 dark:text-red-300">
            Common causes: HMAC secret mismatch between orboto Mail and orboto Control stacks,
            orboto Mail container down (check <code>mail.orboto.io/health</code>),
            or network reach issue.
          </p>
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="shrink-0 rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-xs text-red-700 dark:text-red-300 hover:bg-red-100"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function CustomerTable({
  customers,
  onRowClick,
}: {
  customers: OmsCustomer[];
  onRowClick: (c: OmsCustomer) => void;
}) {
  if (customers.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        No orboto Mail customers match the current search.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2">Email</th>
            <th className="px-3 py-2">Plan</th>
            <th className="px-3 py-2">Usage</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Portal link</th>
            <th className="px-3 py-2 text-right">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {customers.map((c) => (
            <tr
              key={c.id}
              onClick={() => onRowClick(c)}
              className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60"
            >
              <td className="truncate px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                {c.email}
              </td>
              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{c.plan_tier ?? '—'}</td>
              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                <UsageCell
                  used={c.monthly_usage_used}
                  total={c.monthly_usage_total}
                />
              </td>
              <td className="px-3 py-2">
                <StatusPill status={c.status ?? null} />
              </td>
              <td className="px-3 py-2">
                {c.portal_customer_id ? (
                  <a
                    href={`/accounts/${c.portal_customer_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-brand-500 hover:underline"
                  >
                    Portal customer ↗
                  </a>
                ) : (
                  <span className="text-xs text-gray-400">—</span>
                )}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                {c.created_at ? formatDate(c.created_at) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsageCell({
  used,
  total,
}: {
  used: number | null | undefined;
  total: number | null | undefined;
}) {
  if (used == null || total == null) return <span className="text-gray-400">—</span>;
  const pct = total > 0 ? (used / total) * 100 : 0;
  const tone =
    pct >= 95 ? 'text-red-700 dark:text-red-300' : pct >= 80 ? 'text-amber-700 dark:text-amber-300' : 'text-gray-700 dark:text-gray-300';
  return (
    <span className={`tabular-nums ${tone}`}>
      {used.toLocaleString()} / {total.toLocaleString()}
    </span>
  );
}

function StatusPill({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-gray-400">—</span>;
  const tone =
    status === 'active'
      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
      : status === 'suspended'
        ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800'
        : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800';
  return (
    <span className={`inline-flex rounded border px-2 py-0.5 text-xs ${tone}`}>
      {status}
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    return fmtDate(iso);
  } catch {
    return iso;
  }
}
